import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

// Global State Provider for Authentication
import { AuthProvider } from "./context/AuthContext";
import ScrollDownArrow from './components/shared/ScrollDownArrow';

// Components
import Navbar from "./components/Navbar";
import ProtectedRoute from './components/ProtectedRoute';

// Public Pages
import Homepage from './pages/Homepage';
import Services from './pages/Services';
import Authors from './pages/Authors';
import NotFound from './pages/NotFound';
import ThankYou from './pages/ThankYou';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelledPage from './pages/PaymentCancelledPage';
import Community from './pages/Community';
import DirectoryPage from './pages/DirectoryPage';
import ProfileDetailPage from './pages/ProfileDetailPage';
import SelfPublishingService from './pages/SelfPublishingService';
import Course from './pages/Course';
import Corporate from "./pages/Corporate";
import DynamicLandingPage from './pages/DynamicLandingPage';

// Özel Yönlendirme Sayfaları
import MeetSajeda from "./pages/MeetSajeda"; 
import Workshop1 from "./pages/Workshop1";
import Workshop2 from "./pages/Workshop2";
import Workshop3 from "./pages/Workshop3";
import SaminaBook from "./pages/SaminaBook"; 

// Membership Pages
import LoginPage from './membership/LoginPage';
import SignupPage from './membership/SubscriptionPage';
import ProfilePage from "./membership/ProfilePage";

// Admin Pages
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminLogin from './admin/Login';
import ManageEvents from './admin/ManageEvents';
import ManageProfessionals from './admin/ManageProfessionals';
import AddEventForm from "./admin/AddEventForm";
import ManageCampaigns from "./admin/ManageCampaigns";
import StatisticsPage from './admin/StatisticsPage';
import ManageOrdersPage from './admin/ManageOrdersPage';
import TaskBoard from './admin/manage-tasks/TaskBoard';
import ManageBookPages from './admin/ManageBookPages';
import CreateLandingPage from './admin/CreateLandingPage';
import BookLandingPage from './pages/BookLandingPage';
import ManageLandingPages from './admin/ManageLandingPages';

// User Management Pages
import UserListPage from './admin/manage-users/UserListPage';
import EditUserPage from './admin/manage-users/EditUserPage';
import AddUserPage from './admin/manage-users/AddUserPage'; 
import RolePermissionsPage from './admin/manage-users/RolePermissionsPage'; 

import UnsubscribePage from './pages/UnsubscribePage'; 

import ContactPage from './pages/ContactPage';
import ManageMessages from './admin/ManageMessages';
import ManageSubscribers from './admin/ManageSubscribers'; 

const AppLayout = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* Ana Admin Layout'u (Sadece giriş yapmış kullanıcılar girebilir, modül kısıtı yok) */}
        <Route 
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} /> 
          
          {/* Dinamik Yetkilendirilen Rotalar (requiredModule ile çalışır) */}
          <Route path="manage-events" element={<ProtectedRoute requiredModule="manage-events"><ManageEvents /></ProtectedRoute>} />
          <Route path="add-event" element={<ProtectedRoute requiredModule="manage-events"><AddEventForm /></ProtectedRoute>} />
          
          <Route path="manage-professionals" element={<ProtectedRoute requiredModule="manage-professionals"><ManageProfessionals /></ProtectedRoute>} />
          
          <Route path="manage-campaigns" element={<ProtectedRoute requiredModule="manage-campaigns"><ManageCampaigns /></ProtectedRoute>} />
          <Route path="manage-landing-pages" element={<ProtectedRoute requiredModule="manage-book-pages"><ManageLandingPages /></ProtectedRoute>} />
          <Route path="create-landing-page" element={<ProtectedRoute requiredModule="manage-book-pages"><CreateLandingPage /></ProtectedRoute>} />
          <Route path="edit-landing-page/:id" element={<ProtectedRoute requiredModule="manage-book-pages"><CreateLandingPage /></ProtectedRoute>} />
          <Route path="manage-messages" element={<ProtectedRoute requiredModule="manage-messages"><ManageMessages /></ProtectedRoute>} />
          <Route path="manage-subscribers" element={<ProtectedRoute requiredModule="manage-subscribers"><ManageSubscribers /></ProtectedRoute>} />
          
          <Route path="manage-orders" element={<ProtectedRoute requiredModule="manage-orders"><ManageOrdersPage /></ProtectedRoute>} />
          
          <Route path="manage-tasks" element={<ProtectedRoute requiredModule="manage-tasks"><TaskBoard /></ProtectedRoute>} />
          
          <Route path="manage-book-pages" element={<ProtectedRoute requiredModule="manage-book-pages"><ManageBookPages /></ProtectedRoute>} />
          
          <Route path="statistics" element={<ProtectedRoute requiredModule="statistics"><StatisticsPage /></ProtectedRoute>} /> 
          
          {/* Yönetim rotaları sadece sabit olarak admin'e aittir */}
          <Route path="manage-users" element={<ProtectedRoute requiredModule="manage-users"><UserListPage /></ProtectedRoute>} />
          <Route path="manage-users/edit/:userId" element={<ProtectedRoute requiredModule="manage-users"><EditUserPage /></ProtectedRoute>} />
          <Route path="manage-users/add" element={<ProtectedRoute requiredModule="manage-users"><AddUserPage /></ProtectedRoute>} />
          
          {/* Yetkilendirme Sayfası Rotası */}
          <Route path="role-permissions" element={<ProtectedRoute requiredModule="manage-users"><RolePermissionsPage /></ProtectedRoute>} />
        </Route>
      </Routes>
    );
  }

  // --- PUBLIC ROUTES ---
  
  // Sadece dar (container içinde) görünmesini istediğiniz standart sayfalarınız:
  const containerPages = [
    "/services", 
    "/authors", 
    "/thank-you", 
    "/payment-success", 
    "/cancelled", 
    "/profile", 
    "/unsubscribe"
  ];

  // Mevcut URL bu sayfalardan biriyle başlıyor mu kontrol et
  const isContainerPage = containerPages.some(path => location.pathname.startsWith(path));
  
  // Kendi özel tasarımı olan (container gerektirmeyen) sayfalar
  const hasOwnContainer = ["/login", "/signup"].includes(location.pathname);

  // MANTIK: Eğer sayfa "containerPages" içindeyse daralt, değilse (yani /test gibi dinamik bir sayfaysa) tam genişlik yap.
  const mainClassName = (isContainerPage && !hasOwnContainer) ? "mt-4 container" : "";

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar />
      <main className={`flex-grow-1 ${mainClassName}`}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Homepage />} />
          <Route path="/services" element={<Services />} />
          <Route path="/authors/*" element={<Authors />} />
          <Route path="/community" element={<Community />} />
          <Route path="/corporate" element={<Corporate />} />
          <Route path="/directory/:profileId" element={<ProfileDetailPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/self-publishing-service" element={<SelfPublishingService />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/cancelled" element={<PaymentCancelledPage />} />
          <Route path="/contact" element={<ContactPage />} /> 
          <Route path="/course" element={<Course />} />
          
          {/* Özel Landing Pageler */}
          <Route path="/meet-sajeda" element={<MeetSajeda />} />
          <Route path="/workshop-1" element={<Workshop1 />} />
          <Route path="/workshop-2" element={<Workshop2 />} />
          <Route path="/workshop-3" element={<Workshop3 />} />
          <Route path="/samina-book" element={<SaminaBook />} />
          
          {/* Membership Routes */}
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/book/:bookId" element={<BookLandingPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          
          {/* Dinamik Özel Landing Page Rotası (Yukarıdakiler eşleşmezse buraya düşer) */}
          <Route path="/:slug" element={<DynamicLandingPage />} />

          {/* Catch-all Route (Bunun mutlaka en sonda kalması gerek!) */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <ScrollDownArrow /> 
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
    </AuthProvider>
  </HelmetProvider>
);

export default App;